import { ProductFileUpload } from "@theme/product-form";

const GUIDANCE_DIALOG_DELAY_MS = 500;
const POPUP_SELECTOR = "[data-fut-card-personalization-popup]";
const STEP_SELECTOR = "[data-fut-card-personalization-step-block]";
const GUIDANCE_SHOWN_DATASET_KEY = "futCardPlayerGuidanceShown";
const GUIDANCE_BLOCK_SELECTOR = "[data-fut-card-player-image-guidance]";
const GUIDANCE_BLOCK_ID_SELECTOR = "[data-fut-card-player-image-guidance-block-id]";
const GUIDANCE_EDITOR_ANCHOR_SELECTOR =
  "[data-fut-card-player-image-guidance-editor-anchor]";
const GUIDANCE_CLOSE_SELECTOR = "[data-fut-card-player-image-guidance-close]";
const GUIDANCE_EDITOR_OPEN_EVENT = "fut-card:guidance-editor-open";
const GUIDANCE_BLOCK_TYPE = "_fut-card-player-image-guidance";

/**
 * @param {Element | null | undefined} element
 * @returns {string | null}
 */
function readEditorBlockId(element) {
  if (!(element instanceof HTMLElement)) return null;

  const explicitId = element.getAttribute(
    "data-fut-card-player-image-guidance-block-id",
  );
  if (explicitId) return explicitId;

  const editorBlockAttr = element.getAttribute("data-shopify-editor-block");
  if (editorBlockAttr) {
    try {
      const data = JSON.parse(editorBlockAttr);
      if (data?.id) return String(data.id);
    } catch {
      /* ignore malformed editor metadata */
    }
  }

  if (element.id.startsWith("shopify-block-")) {
    return element.id.slice("shopify-block-".length);
  }

  return null;
}

/**
 * @param {string} blockId
 * @returns {string}
 */
function normalizeEditorBlockId(blockId) {
  const separatorIndex = blockId.lastIndexOf("__");
  if (separatorIndex === -1) return blockId;
  return blockId.slice(separatorIndex + 1);
}

/**
 * @param {string | undefined} blockId
 * @param {string | null | undefined} candidateId
 * @returns {boolean}
 */
function editorBlockIdsMatch(blockId, candidateId) {
  if (!blockId || !candidateId) return false;

  const normalizedBlockId = normalizeEditorBlockId(blockId);
  const normalizedCandidateId = normalizeEditorBlockId(candidateId);

  return (
    blockId === candidateId ||
    blockId === normalizedCandidateId ||
    normalizedBlockId === candidateId ||
    normalizedBlockId === normalizedCandidateId ||
    blockId.endsWith(`__${normalizedCandidateId}`) ||
    candidateId.endsWith(`__${normalizedBlockId}`)
  );
}

/**
 * @returns {HTMLElement[]}
 */
function getGuidanceEditorElements() {
  return Array.from(
    document.querySelectorAll(
      `${GUIDANCE_BLOCK_SELECTOR}, ${GUIDANCE_EDITOR_ANCHOR_SELECTOR}`,
    ),
  ).filter((element) => element instanceof HTMLElement);
}

/**
 * @param {string | undefined} blockId
 * @param {Element | null} [target]
 * @returns {HTMLElement | null}
 */
function findGuidanceBlockRoot(blockId, target = null) {
  if (target instanceof Element) {
    const targetRoot = target.closest(GUIDANCE_BLOCK_SELECTOR);
    if (targetRoot instanceof HTMLElement) return targetRoot;

    const anchorRoot = target.closest(GUIDANCE_EDITOR_ANCHOR_SELECTOR);
    if (anchorRoot instanceof HTMLElement) {
      const siblingRoot = anchorRoot.nextElementSibling;
      if (siblingRoot instanceof HTMLElement && siblingRoot.matches(GUIDANCE_BLOCK_SELECTOR)) {
        return siblingRoot;
      }
    }
  }

  if (!blockId) return null;

  for (const candidate of getGuidanceEditorElements()) {
    const candidateId = readEditorBlockId(candidate);
    if (!editorBlockIdsMatch(blockId, candidateId)) continue;

    if (candidate.matches(GUIDANCE_BLOCK_SELECTOR)) {
      return candidate;
    }

    const siblingRoot = candidate.nextElementSibling;
    if (siblingRoot instanceof HTMLElement && siblingRoot.matches(GUIDANCE_BLOCK_SELECTOR)) {
      return siblingRoot;
    }
  }

  return null;
}

/**
 * @param {string | undefined} blockId
 * @returns {boolean}
 */
function blockIdLooksLikeGuidanceBlock(blockId) {
  if (!blockId) return false;

  const normalizedBlockId = normalizeEditorBlockId(blockId);

  return (
    normalizedBlockId.includes("player-image-guidance") ||
    normalizedBlockId.includes("player_image_guidance") ||
    blockId.includes(GUIDANCE_BLOCK_TYPE)
  );
}

/**
 * @param {string | undefined} blockId
 * @param {Element | null} [target]
 * @returns {boolean}
 */
function isGuidanceEditorBlockSelection(blockId, target = null) {
  if (findGuidanceBlockRoot(blockId, target)) return true;

  if (target instanceof Element) {
    if (
      target.closest(
        `${GUIDANCE_BLOCK_SELECTOR}, ${GUIDANCE_EDITOR_ANCHOR_SELECTOR}`,
      )
    ) {
      return true;
    }
  }

  if (blockIdLooksLikeGuidanceBlock(blockId)) return true;

  if (!blockId) return false;

  for (const candidate of document.querySelectorAll(GUIDANCE_BLOCK_ID_SELECTOR)) {
    if (!(candidate instanceof HTMLElement)) continue;

    const candidateId = candidate.getAttribute(
      "data-fut-card-player-image-guidance-block-id",
    );
    if (editorBlockIdsMatch(blockId, candidateId)) return true;
  }

  return false;
}

/**
 * @param {HTMLElement | null | undefined} step
 * @returns {HTMLDialogElement | null}
 */
function getGuidanceDialogInStep(step) {
  if (!(step instanceof HTMLElement)) return null;

  const dialog = step.querySelector(
    `${GUIDANCE_BLOCK_SELECTOR} dialog.fut-card-player-image-guidance`,
  );

  return dialog instanceof HTMLDialogElement ? dialog : null;
}

/**
 * @param {HTMLElement | null | undefined} step
 * @param {{ forEditor?: boolean, skipShownMark?: boolean }} [options]
 */
function openGuidanceDialogInStep(step, options = {}) {
  const dialog = getGuidanceDialogInStep(step);
  if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

  if (!options.skipShownMark) {
    const popup = step?.closest(POPUP_SELECTOR);
    if (popup instanceof HTMLElement) {
      popup.dataset[GUIDANCE_SHOWN_DATASET_KEY] = "true";
    }
  }

  if (options.forEditor) {
    dialog.dataset.editorOpen = "true";
  }

  dialog.showModal();
}

/**
 * @param {string | undefined} blockId
 * @param {Element | null} [target]
 */
function openGuidanceForEditorSelection(blockId, target = null) {
  const guidanceRoot = findGuidanceBlockRoot(blockId, target);
  const step =
    guidanceRoot?.closest(STEP_SELECTOR) ??
    target?.closest(STEP_SELECTOR) ??
    null;

  if (!(step instanceof HTMLElement)) return;

  const upload = step.querySelector("fut-card-player-image-upload");
  if (upload instanceof FutCardPlayerImageUpload) {
    upload.openGuidanceForEditor();
    return;
  }

  openGuidanceDialogInStep(step, { forEditor: true, skipShownMark: true });
}

/**
 * @param {string | undefined} blockId
 * @param {Element | null} [target]
 * @param {number} [attempt]
 */
function openGuidanceForEditorSelectionWhenReady(
  blockId,
  target = null,
  attempt = 0,
) {
  const popup = document.querySelector(POPUP_SELECTOR);
  const popupOpen =
    popup instanceof HTMLElement &&
    !popup.hasAttribute("hidden") &&
    popup.getAttribute("aria-hidden") !== "true";

  if (!popupOpen && attempt < 40) {
    window.setTimeout(
      () => openGuidanceForEditorSelectionWhenReady(blockId, target, attempt + 1),
      50,
    );
    return;
  }

  openGuidanceForEditorSelection(blockId, target);
}

/**
 * FUT card player photo upload with post-upload guidance dialog.
 *
 * @extends {ProductFileUpload}
 */
class FutCardPlayerImageUpload extends ProductFileUpload {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {number | undefined} */
  #guidanceDialogTimeout;
  /** @type {HTMLElement | null} */
  #stepElement = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#syncGuidanceFromStep();
    this.#bindGuidanceDialogEvents();

    this.addEventListener(
      "product-file-upload:preview-change",
      this.#handlePreviewChange,
      { signal },
    );

    this.#stepElement = this.closest(STEP_SELECTOR);
    this.#stepElement?.addEventListener("click", this.#handleStepClick, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#clearGuidanceDialogTimeout();
    this.#closeGuidanceDialog();
    this.#stepElement = null;
  }

  handleCloseGuidance() {
    this.#closeGuidanceDialog();
  }

  openGuidanceForEditor() {
    this.#syncGuidanceFromStep();

    this.#clearGuidanceDialogTimeout();
    const step = this.closest(STEP_SELECTOR);
    openGuidanceDialogInStep(step instanceof HTMLElement ? step : null, {
      forEditor: true,
      skipShownMark: true,
    });
    this.#bindGuidanceDialogEvents();
  }

  /** @param {Event} event */
  #handleStepClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(GUIDANCE_CLOSE_SELECTOR)) return;

    event.preventDefault();
    this.handleCloseGuidance();
  };

  /** @param {Event} event */
  #handleGuidanceCancel = (event) => {
    event.preventDefault();
  };

  #handleGuidanceClose = () => {
    const dialog = this.#getGuidanceDialog();
    if (dialog instanceof HTMLDialogElement) {
      delete dialog.dataset.editorOpen;
    }
  };

  #handlePreviewChange = () => {
    this.#clearGuidanceDialogTimeout();

    if (!this.hasAttribute("file-selected")) return;
    if (this.dataset.showGuidance !== "true") return;
    if (this.#hasShownGuidanceThisVisit()) return;

    this.#guidanceDialogTimeout = window.setTimeout(() => {
      this.#guidanceDialogTimeout = undefined;
      const step = this.closest(STEP_SELECTOR);
      openGuidanceDialogInStep(step instanceof HTMLElement ? step : null);
    }, GUIDANCE_DIALOG_DELAY_MS);
  };

  #syncGuidanceFromStep() {
    const step = this.closest(STEP_SELECTOR);
    const guidanceRoot = step?.querySelector(
      `${GUIDANCE_BLOCK_SELECTOR}[data-guidance-enabled="true"]`,
    );

    if (guidanceRoot instanceof HTMLElement) {
      this.dataset.showGuidance = "true";
      return;
    }

    delete this.dataset.showGuidance;
  }

  #bindGuidanceDialogEvents() {
    const dialog = this.#getGuidanceDialog();
    if (!(dialog instanceof HTMLDialogElement)) return;

    const { signal } = this.#abortController ?? {};
    if (!signal) return;

    dialog.addEventListener("cancel", this.#handleGuidanceCancel, { signal });
    dialog.addEventListener("close", this.#handleGuidanceClose, { signal });
  }

  /**
   * @returns {HTMLDialogElement | null}
   */
  #getGuidanceDialog() {
    const step = this.closest(STEP_SELECTOR);
    return getGuidanceDialogInStep(step instanceof HTMLElement ? step : null);
  }

  /** @returns {boolean} */
  #hasShownGuidanceThisVisit() {
    const popup = this.closest(POPUP_SELECTOR);
    return popup instanceof HTMLElement && popup.dataset[GUIDANCE_SHOWN_DATASET_KEY] === "true";
  }

  #clearGuidanceDialogTimeout() {
    if (this.#guidanceDialogTimeout === undefined) return;

    clearTimeout(this.#guidanceDialogTimeout);
    this.#guidanceDialogTimeout = undefined;
  }

  #closeGuidanceDialog() {
    this.#clearGuidanceDialogTimeout();

    const dialog = this.#getGuidanceDialog();
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    delete dialog.dataset.editorOpen;
    dialog.close();
  }
}

if (!customElements.get("fut-card-player-image-upload")) {
  customElements.define(
    "fut-card-player-image-upload",
    FutCardPlayerImageUpload,
  );
}

/** @type {boolean} */
let guidanceEditorEventsBound = false;

function ensureFutCardPlayerImageGuidanceEditorEvents() {
  if (guidanceEditorEventsBound) return;

  guidanceEditorEventsBound = true;

  document.addEventListener("shopify:block:select", (event) => {
    if (!window.Shopify?.designMode) return;

    /** @type {{ blockId?: string } | undefined} */
    const detail =
      "detail" in event
        ? /** @type {{ blockId?: string }} */ (event.detail)
        : undefined;
    const blockId = detail?.blockId;
    const target = event.target instanceof Element ? event.target : null;

    if (!isGuidanceEditorBlockSelection(blockId, target)) return;

    openGuidanceForEditorSelectionWhenReady(blockId, target);
  });

  document.addEventListener(GUIDANCE_EDITOR_OPEN_EVENT, (event) => {
    if (!window.Shopify?.designMode) return;
    if (!(event instanceof CustomEvent)) return;

    /** @type {{ blockId?: string, target?: Element | null }} */
    const detail = event.detail ?? {};

    openGuidanceForEditorSelectionWhenReady(
      detail.blockId,
      detail.target ?? null,
    );
  });
}

ensureFutCardPlayerImageGuidanceEditorEvents();

// @ts-expect-error Theme editor bridge for cross-module guidance preview.
window.__openFutCardGuidanceEditor = openGuidanceForEditorSelectionWhenReady;
// @ts-expect-error Theme editor bridge for cross-module guidance preview.
window.__isFutCardGuidanceEditorBlockSelection = isGuidanceEditorBlockSelection;

export {
  FutCardPlayerImageUpload,
  isGuidanceEditorBlockSelection,
  openGuidanceForEditorSelectionWhenReady,
};
